import { useEffect } from "react";
import { ConfiguratorCanvas } from "./configurator/MirrorScene";
import { ConfiguratorPanel } from "./configurator/ConfiguratorPanel";
import { DEFAULT_LOGO_TRANSFORM } from "./configurator/types";
import {
  getState,
  initStore,
  setState as storeSetState,
  updateState,
} from "./configurator/configuratorStore";
import { onCpbChange, onCpbLogoTool, onCpbReady, resolveLayersAsChanged } from "./cpbBridge";
import { mapCpbEntriesToPatch } from "./configurator/cpbStateMapper";
import "./App.css";

initStore();

/**
 * App is a *passive* bridge: CPB events → store, store → canvas/panel via
 * their own slice subscriptions. App does NOT subscribe to the store itself.
 *
 * Subscribing here (the prior `useConfigurator(s => s)`) re-rendered App on
 * every patch and handed fresh `state` props down to <ConfiguratorCanvas /> /
 * <ConfiguratorPanel />, which forced the entire MirrorGroup subtree to
 * reconcile (and recompute its useMemo dep arrays) on every keystroke. With
 * App passive, only components whose own slice actually changed re-render.
 */
export default function App() {
  // Revoke any leftover blob: URL from a logo upload on unmount. Reads via
  // getState() so App doesn't need a subscription just for cleanup.
  useEffect(
    () => () => {
      const u = getState().logoImageUrl;
      if (u?.startsWith("blob:")) URL.revokeObjectURL(u);
    },
    [],
  );

  useEffect(() => {
    const apply = (
      entries: Parameters<typeof mapCpbEntriesToPatch>[0],
      layers: Parameters<typeof mapCpbEntriesToPatch>[1],
      source: string,
    ) => {
      const { patch, unmapped } = mapCpbEntriesToPatch(entries, layers);
      if (Object.keys(patch).length > 0) {
        console.log(
          `%c[viewer] ${source} → patch:`,
          "color:#e9a;font-weight:bold;",
          patch,
        );
        storeSetState(patch);
      }
      if (unmapped.length > 0) {
        console.warn(
          `%c[viewer] ${source}: unmapped CPB categories:`,
          "color:#e9a;font-weight:bold;",
          unmapped.map((u) => ({
            panel: u.category.panelTitle,
            category: u.category.title,
            label: u.next?.label,
            layerValue: u.next?.layerValue,
          })),
        );
      }
    };

    const offReady = onCpbReady(({ product, layers }) => {
      apply(resolveLayersAsChanged(layers, product), layers, "cpb ready");
    });
    const offChange = onCpbChange(({ changed, layers }) => {
      apply(changed, layers, "cpb change");
    });
    return () => {
      offReady();
      offChange();
    };
  }, []);

  useEffect(() => {
    const offLogoTool = onCpbLogoTool(({ action, step }) => {
      const clampStep = Math.min(200, Math.max(1, step));
      updateState((prev) => {
        if (prev.designMode !== "logo") return prev;
        let logoTransform = prev.logoTransform;
        switch (action) {
          case "magnifier-plus":
            logoTransform = {
              ...logoTransform,
              scalePercent: Math.min(400, logoTransform.scalePercent + clampStep),
            };
            break;
          case "magnifier-minus":
            logoTransform = {
              ...logoTransform,
              scalePercent: Math.max(20, logoTransform.scalePercent - clampStep),
            };
            break;
          case "undo":
            logoTransform = { ...logoTransform, rotationDeg: logoTransform.rotationDeg - clampStep };
            break;
          case "redo":
            logoTransform = { ...logoTransform, rotationDeg: logoTransform.rotationDeg + clampStep };
            break;
          case "chevron-left":
            logoTransform = { ...logoTransform, offsetPxX: logoTransform.offsetPxX - clampStep };
            break;
          case "chevron-right":
            logoTransform = { ...logoTransform, offsetPxX: logoTransform.offsetPxX + clampStep };
            break;
          case "chevron-up":
            logoTransform = { ...logoTransform, offsetPxY: logoTransform.offsetPxY + clampStep };
            break;
          case "chevron-down":
            logoTransform = { ...logoTransform, offsetPxY: logoTransform.offsetPxY - clampStep };
            break;
          case "sync":
            logoTransform = { ...logoTransform, flipH: !logoTransform.flipH };
            break;
          case "chevron-height":
            logoTransform = { ...DEFAULT_LOGO_TRANSFORM };
            break;
          case "trash":
            return { ...prev, logoImageUrl: null, logoAssetRevision: "", logoTransform: { ...DEFAULT_LOGO_TRANSFORM } };
          default:
            return prev;
        }
        return { ...prev, logoTransform };
      });
    });
    return () => offLogoTool();
  }, []);

  return (
    <div className="app-root">
      <div className="canvas-wrap">
        <ConfiguratorCanvas />
      </div>
      <ConfiguratorPanel />
    </div>
  );
}
